import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { Alert } from "../../../../../controls/alert";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { useSearchUser } from "../../hooks/useSearchUser";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { User } from "../../models/User";
import { deleteUser } from "../../repositories/UsersRepository";
import { AssetSelector } from "../common/AssetSelector";
import { AuthTypeSelector } from "../common/AuthTypeSelector";
import { ProfilesPicker } from "../common/ProfilesPicker";

type Props = {
	userId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const DeleteUser = ({ userId, show, onHide, suffixTitle }: Props) => {
	const [open, setOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [deleteAlert, setDeleteAlert] = useState(false);
	const [error, setError] = useState("");
	const [profileError, setprofileError] = useState("");

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		isSuccess,
	} = useSearchUser({
		userId,
	});

	const { register, control, setValue } = useForm<User>({
		defaultValues: INITIAL_VALUES,
	});

	const handleClose = (
		event?: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
		setDeleteAlert(false);
	};

	const deleteUserr = useMutation({
		mutationFn: deleteUser,
		onSuccess: () => {
			setOpen(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setDeleteAlert(false);
			setIsSubmitLoading(false);
		},
	});

	const onSubmit = async (userSelected: number) => {
		setIsSubmitLoading(true);
		await deleteUserr.mutate(userSelected);
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			if (isSuccess) {
				const userSelected = rows ? rows[0] : null;
				if (userSelected) {
					const profilesIds =
						(userSelected!.ProfilesId?.toString() || "").split(",") || [];

					setValue("UserId", userSelected!.UserId);
					setValue("UserName", userSelected!.UserName);
					setValue("FirstName", userSelected!.FirstName);
					setValue("LastName", userSelected!.LastName);
					setValue("AuthTypeId", userSelected!.AuthTypeId);
					setValue("IsActive", userSelected!.IsActive);
					setValue("LastLogin", userSelected!.LastLogin);
					setValue("DefaultAssetId", userSelected!.DefaultAssetId);
					setValue(
						"ProfilesId",
						profilesIds.map((pId: string) => parseInt(pId)),
					);
				}
			}
		}
	}, [show, isSuccess, rows, userId]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Delete User${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<MesfModal
				title="DELETE USER"
				open={show}
				handleClose={() => onHide(false)}
				id="delete-user-modal"
				maxWidth="md"
			>
				<form>
					<MesfModal.Content>
						{isLoading && (
							<Grid2
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								<LazyLoading />
							</Grid2>
						)}
						{!isLoading && (
							<>
								<Grid2 container spacing={1} alignItems="center">
									<Grid2 size={{ xs: 12 }}>
										<TextField
											{...register("UserName", { required: true })}
											label="Username"
											disabled
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid2>
									<Grid2 size={{ md: 6, xs: 12 }}>
										<TextField
											{...register("FirstName", { required: true })}
											label="First name"
											variant="outlined"
											disabled
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid2>
									<Grid2 size={{ md: 6, xs: 12 }}>
										<TextField
											{...register("LastName", { required: true })}
											label="Last name"
											variant="outlined"
											disabled
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid2>
									<Grid2 container size={{ xs: 12 }} spacing={3}>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="ProfilesId"
												control={control}
												rules={{ required: true }}
												render={({ field: { onChange, value } }) => (
													<ProfilesPicker
														onChange={onChange}
														value={value as number[]}
														userIdForDelete={null}
														setprofileError={setprofileError}
														disabled={true}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="AuthTypeId"
												control={control}
												render={({ field: { value, onChange } }) => (
													<AuthTypeSelector
														value={value}
														onChange={onChange}
														disabled={true}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="DefaultAssetId"
												control={control}
												render={({ field: { value, onChange } }) => (
													<AssetSelector
														value={value}
														onChange={onChange}
														disabled={true}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="IsActive"
												control={control}
												render={({ field }) => (
													<FormControlLabel
														control={
															<Checkbox
																checked={field.value}
																onChange={field.onChange}
																name="IsActive"
																color="primary"
																disabled
															/>
														}
														label="Active"
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									CANCEL
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="secondary"
									onClick={() => setDeleteAlert(true)}
								>
									DELETE
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="warning" onClose={handleClose}>
					The user was deleted successfully
				</Alert>
			</Snackbar>
			<MesfModal
				open={deleteAlert}
				title="WARNING"
				handleClose={() => handleClose()}
				id="responsive-dialog-title"
			>
				<MesfModal.Content>
					Be careful, once deleted you will not be able to recover this user,
					are you sure you want to delete it?
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setDeleteAlert(false)}
						variant="contained"
						color="inherit"
					>
						CANCEL
					</Button>
					<Button
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						variant="contained"
						color="secondary"
						onClick={() => onSubmit(userId!)}
					>
						Delete
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Deleting User"
			/>
			<ErrorModal
				error={profileError}
				onHide={() => setprofileError("")}
				title="Error Choosing Profile"
			/>
		</>
	);
};
